import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getScoreColor, getScoreLabel, channelIcons } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiImage, FiTrendingUp, FiDollarSign, FiTarget, FiEye } = FiIcons;

const MediaCard = ({ media, index }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    console.log('Navigating to media detail:', media.id); // Debug log
    navigate(`/media/${media.id}`);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    console.log('View details clicked for media:', media.id); // Debug log
    navigate(`/media/${media.id}`);
  };

  // Calculate overall metrics
  const calculateOverallScore = (channels) => {
    const scores = Object.values(channels).map(channel => channel.score);
    return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  };

  const calculateTotalRevenue = (channels) => {
    return Object.values(channels).reduce((sum, channel) => sum + channel.revenue, 0);
  };

  const calculateTotalSpent = (channels) => {
    return Object.values(channels).reduce((sum, channel) => sum + channel.spent, 0);
  };

  const calculateAverageRoas = (channels) => {
    const roas = Object.values(channels).map(channel => channel.roas);
    return (roas.reduce((sum, r) => sum + r, 0) / roas.length).toFixed(1);
  };

  const overallScore = calculateOverallScore(media.channels);
  const totalRevenue = calculateTotalRevenue(media.channels);
  const totalSpent = calculateTotalSpent(media.channels);
  const averageRoas = calculateAverageRoas(media.channels);

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      onClick={handleClick}
    >
      {/* Thumbnail Section */}
      <div className="relative h-48 bg-gray-100">
        <img 
          src={media.thumbnail} 
          alt={media.name} 
          className="w-full h-full object-cover"
        />
        
        {/* Media Type Badge */}
        <div className="absolute top-3 right-3">
          <div className="flex items-center space-x-1 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs">
            <SafeIcon icon={media.type === 'video' ? FiPlay : FiImage} className="h-3 w-3" />
            <span>{media.type === 'video' ? 'วิดีโอ' : 'รูปภาพ'}</span>
          </div>
        </div>

        {/* Overall Score Badge */}
        <div className="absolute top-3 left-3">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${getScoreColor(overallScore)}`}>
            {overallScore} คะแนน
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* Title and Product */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">{media.name}</h3>
          <p className="text-sm text-gray-500">{media.product}</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <SafeIcon icon={FiTrendingUp} className="h-4 w-4 text-green-500 mr-1" />
            </div>
            <div className="text-sm font-semibold text-gray-900">{averageRoas}x</div>
            <div className="text-xs text-gray-500">ROAS เฉลี่ย</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <SafeIcon icon={FiDollarSign} className="h-4 w-4 text-blue-500 mr-1" />
            </div>
            <div className="text-sm font-semibold text-gray-900">฿{totalRevenue.toLocaleString()}</div>
            <div className="text-xs text-gray-500">รายได้</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <SafeIcon icon={FiTarget} className="h-4 w-4 text-purple-500 mr-1" />
            </div>
            <div className="text-sm font-semibold text-gray-900">฿{totalSpent.toLocaleString()}</div>
            <div className="text-xs text-gray-500">งบประมาณ</div>
          </div>
        </div>

        {/* Channel Performance */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">ช่องทางที่ใช้</h4>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(media.channels).map(([channelId, channel]) => (
              <div key={channelId} className="flex items-center space-x-1">
                <span className="text-lg" title={channel.name}>{channelIcons[channelId]}</span>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getScoreColor(channel.score)}`}>
                  {channel.score}
                </span>
              </div>
            ))}
          </div>

          {/* Performance Status */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">สถานะ</span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${getScoreColor(overallScore)}`}>
                {getScoreLabel(overallScore)}
              </span>
            </div>
          </div>

          {/* View Details Button */}
          <div className="pt-3">
            <motion.button
              onClick={handleViewDetails}
              className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiEye} className="h-4 w-4" />
              <span>ดูรายละเอียด</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MediaCard;